/**
 * PR4 (fix smoke) — Configuración: "Contacto para avisos de reservas".
 * Expone el contacto configurable de V121 (que ya vivía solo en el panel de la
 * campana, poco descubrible). El aviso de cancelación/reagenda es IN-APP (no
 * correo): esto elige a quién se le ASIGNA. Reusa GET/PUT /me/notifications/settings.
 * Auto-gateado: si la API 403ea (sin bookings:manage) el card no se muestra.
 */
import { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { api } from '../lib/api';

interface Settings { notifyUserId: string | null; users: Array<{ id: string; name: string; email: string }>; }

export function BookingNotifyContactCard() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [hidden, setHidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api('/me/notifications/settings')
      .then((r) => setSettings(r as Settings))
      .catch(() => setHidden(true)); // sin permiso/módulo → no se muestra
  }, []);

  const change = async (userId: string) => {
    const v = userId || null;
    setSettings((s) => (s ? { ...s, notifyUserId: v } : s));
    setSaving(true); setSaved(false);
    try {
      await api('/me/notifications/settings', { method: 'PUT', body: { notifyUserId: v } });
      setSaved(true);
    } catch { /* la API valida; se recargará al reabrir */ }
    finally { setSaving(false); }
  };

  if (hidden || !settings) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-slate-900/5 p-2 text-slate-600"><BellRing className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-lg text-slate-900">Contacto para avisos de reservas</h3>
          <p className="mt-0.5 text-sm text-slate-500">
            A quién se le asigna el aviso <b>in-app</b> (la campana 🔔) cuando un cliente cancela o
            reagenda — incluye el aviso fiscal si la reserva tenía factura. No se envía correo.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select value={settings.notifyUserId ?? ''} onChange={(e) => change(e.target.value)} disabled={saving}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Todo el equipo (cualquier admin lo ve)</option>
              {settings.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {saving ? <span className="text-xs text-slate-400">Guardando…</span>
              : saved ? <span className="text-xs text-emerald-600">Guardado ✓</span> : null}
          </div>
        </div>
      </div>
    </section>
  );
}
